import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Pencil, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RupiahInput } from "@/components/RupiahInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const initVals = (fields, values) => Object.fromEntries(fields.map((f) => {
  const v = values?.[f.k];
  return [f.k, v == null ? "" : f.t === "date" ? String(v).slice(0, 10) : String(v)];
}));

function EditDialog({ open, setOpen, title, fields, values, path, onDone, testId }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (open) setF(initVals(fields, values)); }, [open, fields, values]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = Object.fromEntries(fields.map((x) => [x.k, x.t === "money" ? Number(f[x.k]) || 0 : f[x.k]]));
      await api.put(path, body);
      toast.success("Data diperbarui."); setOpen(false); onDone?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent onClick={(e) => e.stopPropagation()} data-testid={`${testId}-edit-dialog`}>
        <DialogHeader><DialogTitle>{title || "Edit Data"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          {fields.map((x) => (
            <div key={x.k}>
              <Label>{x.l}</Label>
              {x.t === "money" ? (
                <RupiahInput value={f[x.k] ?? ""} onChange={(e) => setF((p) => ({ ...p, [x.k]: e.target.value }))} className="mt-1.5 font-mono" data-testid={`${testId}-field-${x.k}`} />
              ) : (
                <Input type={x.t === "date" ? "date" : "text"} value={f[x.k] ?? ""} onChange={(e) => setF((p) => ({ ...p, [x.k]: e.target.value }))} className="mt-1.5" data-testid={`${testId}-field-${x.k}`} />
              )}
            </div>
          ))}
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid={`${testId}-edit-save`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AdminDeleteButton({ path, onDone, label = "Hapus", confirmText, testId, iconOnly }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (user?.role !== "it_admin") return null;
  const run = async () => {
    setBusy(true);
    try { await api.delete(path); toast.success(`${label} berhasil.`); setOpen(false); onDone?.(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button type="button" size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`h-7 text-rose-600 hover:text-rose-700 hover:bg-rose-50 ${iconOnly ? "w-7 p-0" : "px-2"}`} title={label} data-testid={`${testId}-delete-btn`}>
        <Trash2 className="h-3.5 w-3.5" />{!iconOnly && <span className="ml-1 text-xs">{label}</span>}
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()} data-testid={`${testId}-confirm-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{label}?</AlertDialogTitle>
            <AlertDialogDescription>{confirmText || "Data akan dihapus permanen beserta semua efeknya (saldo, status, laporan) dan tidak dapat dikembalikan."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testId}-confirm-cancel`}>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); run(); }} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid={`${testId}-confirm-ok`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ya, Hapus Permanen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function AdminActions({ editPath, deletePath, fields, values, onDone, title, deleteLabel, confirmText, testId }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (user?.role !== "it_admin") return null;
  return (
    <span className="inline-flex items-center gap-0.5" onClick={(e) => e.stopPropagation()} data-testid={`${testId}-admin-actions`}>
      {editPath && fields?.length > 0 && (
        <>
          <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(true)} className="h-7 w-7 p-0 text-brand" title="Edit" data-testid={`${testId}-edit-btn`}><Pencil className="h-3.5 w-3.5" /></Button>
          <EditDialog open={open} setOpen={setOpen} title={title} fields={fields} values={values} path={editPath} onDone={onDone} testId={testId} />
        </>
      )}
      {deletePath && <AdminDeleteButton path={deletePath} onDone={onDone} label={deleteLabel} confirmText={confirmText} testId={testId} iconOnly={!deleteLabel} />}
    </span>
  );
}
